import * as msal from "@azure/msal-browser";
import { combine } from "./utils";
import { Domain, getDomain } from "@/utils/domain";
import { logger } from "@/lib/logger";

const redirectUri =
  getDomain(window.location.host) === Domain.LODA
    ? import.meta.env.VITE_LODA_MICROSOFT_AAD_REDIRECT_URI
    : import.meta.env.VITE_LARA_MICROSOFT_AAD_REDIRECT_URI;

const msalParams: msal.Configuration = {
  auth: {
    authority: "https://login.microsoftonline.com/common", // common allows both personal and work/school accounts otherwise use organizations to restrict it to work/school accounts
    clientId:
      getDomain(window.location.host) === Domain.LODA
        ? import.meta.env.VITE_LODA_MICROSOFT_AAD_CLIENT_ID
        : import.meta.env.VITE_LARA_MICROSOFT_AAD_CLIENT_ID,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    // Default is sessionStorage, which forces a Microsoft login popup in every
    // new tab (266 `no_account_error`s / 30d in prod). msal-browser v5 encrypts
    // localStorage entries with a session-cookie key, so tokens do not outlive
    // the browser session. The manual app logout (`handleLogout` in
    // utils/auth.ts, both domains) clears localStorage; SDK-driven Auth0
    // sign-outs do not, so `bindMsalCacheToAuth0User` ties the cache to the
    // Auth0 user who linked it and clears it when someone else signs in.
    cacheLocation: msal.BrowserCacheLocation.LocalStorage,
  },
};

export const app = new msal.PublicClientApplication(msalParams);

let initializationComplete = false;
let initPromise: Promise<void> | null = null;

const getTenantAuthorityFor = (account?: msal.AccountInfo | null) => {
  const tid = account?.tenantId || account?.idTokenClaims?.tid || "common";
  return `https://login.microsoftonline.com/${tid}`;
};

/**
 * Resolve the account to use for a token request. MSAL only sets an active
 * account after an interactive login in this tab; with a persistent cache the
 * account may already exist without being active, so fall back to it.
 * Must be called after `ensureInitialized()`.
 */
const getAccountForRequest = (): msal.AccountInfo | undefined => {
  const active = app.getActiveAccount();
  if (active) return active;

  const first = app.getAllAccounts()[0];
  if (first) {
    app.setActiveAccount(first);
    return first;
  }

  return undefined;
};

export const ensureInitialized = async (): Promise<void> => {
  if (initializationComplete) {
    return Promise.resolve();
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = app
    .initialize()
    .then(() => {
      initializationComplete = true;
    })
    .catch((error) => {
      logger.error("MSAL initialization failed", {
        operation: "ensureInitialized",
        error,
      });
      initPromise = null;
      throw error;
    });

  return initPromise;
};

export const getSharePointUrl = async (
  accessToken: string
): Promise<string | null> => {
  try {
    const response = await fetch(
      "https://graph.microsoft.com/v1.0/sites/root",
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching SharePoint URL: ${response.statusText}`);
    }

    const data = await response.json();
    return data.webUrl;
  } catch (error) {
    logger.error("Failed to fetch SharePoint root site URL from Graph API", {
      operation: "getSharePointUrl",
      error,
    });
    return null;
  }
};

/**
 * True once MSAL has initialised and holds at least one cached account.
 * Deliberately synchronous: `launchPicker` reads it between the user's click
 * and `window.open`, and any await there risks the popup blocker. Before
 * initialisation it reports false, so callers fall back to the login path.
 */
export const hasCachedAccount = (): boolean =>
  initializationComplete && app.getAllAccounts().length > 0;

type PopupOptions = { authority?: string; claims?: string };

/** Interactive token request; throws MSAL's error so callers can classify it. */
const acquireTokenViaPopup = async (
  scopes: string[],
  options: PopupOptions
): Promise<string | null> => {
  const popupParams: msal.PopupRequest = {
    scopes,
    authority: options.authority,
    claims: options.claims,
  };
  const popupResult = await app.acquireTokenPopup(popupParams);
  app.setActiveAccount(popupResult.account);
  return popupResult.accessToken || null;
};

export const tokenPopup = async (
  scopes: string[],
  options: PopupOptions = {}
): Promise<string | null> => {
  await ensureInitialized();

  try {
    return await acquireTokenViaPopup(scopes, options);
  } catch (popupError) {
    // Typically user-cancelled popup or popup blocked — expected, not an
    // alertable error. Log as warn so callers can decide whether to surface a
    // toast.
    logger.warn("MSAL token popup did not complete", {
      operation: "tokenPopup",
      error: popupError,
    });
    return null;
  }
};

/**
 * MSAL/AAD error codes that mean the user, their tenant admin, or their
 * browser said no: a dismissed popup, "Need admin approval", declined consent,
 * a popup blocker. Expected outcomes, not application failures (LAR-403).
 */
const DECLINED_MSAL_ERROR_CODES = new Set([
  "user_cancelled",
  "access_denied",
  "consent_required",
  "interaction_required",
  "login_required",
  "popup_window_error",
  "empty_window_error",
]);

/** `errorCode` of an MSAL `AuthError` (or AAD server error), else null. */
export const getMsalErrorCode = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null;
  const code = (error as { errorCode?: unknown }).errorCode;
  return typeof code === "string" && code !== "" ? code : null;
};

export const isDeclinedMsalError = (error: unknown): boolean => {
  const code = getMsalErrorCode(error);
  return code !== null && DECLINED_MSAL_ERROR_CODES.has(code);
};

/**
 * Token types we know. Microsoft's picker samples document "SharePoint" and
 * "Graph", but the live picker also sends "SharePoint_MSAL" (observed 2026-09),
 * so callers relaying picker commands should go through {@link normalizeTokenType}.
 */
export const TOKEN_TYPES = [
  "SharePoint",
  "SharePoint_SelfIssued",
  "SharePoint_MSAL",
  "Graph",
] as const;
export type TokenType = (typeof TOKEN_TYPES)[number];

export const isTokenType = (value: unknown): value is TokenType =>
  typeof value === "string" &&
  (TOKEN_TYPES as readonly string[]).includes(value);

/**
 * Maps whatever `type` the picker sends onto a token type we can serve:
 * known values pass through, anything mentioning Graph is a Graph token, any
 * other string is treated as a SharePoint token (the resource decides the
 * audience anyway). Non-strings are rejected.
 */
export const normalizeTokenType = (value: unknown): TokenType | null => {
  if (isTokenType(value)) return value;
  if (typeof value !== "string" || value.length === 0) return null;
  return value.toLowerCase().includes("graph") ? "Graph" : "SharePoint";
};

/**
 * A token request. `scopes` are resource-relative (e.g. `"AllSites.Read"`).
 * The File Picker's own `authenticate` command carries no `scopes`, so callers
 * relaying it get the resource's `.default` scope (Microsoft's sample does the same).
 */
export type TokenCommand = {
  type: TokenType;
  resource: string;
  scopes?: string[];
  claimsChallenge?: string;
};

const GRAPH_DEFAULT_SCOPE = "https://graph.microsoft.com/.default";

/**
 * Scopes are issued per host. The picker may send a resource with a path
 * (e.g. `/personal/user`), so reduce it to its origin before building scopes.
 */
const resourceOrigin = (resource: string): string => {
  try {
    return new URL(resource).origin;
  } catch {
    return resource;
  }
};

export const getDefaultScopeFor = (
  command: Pick<TokenCommand, "type" | "resource">
): string =>
  command.type === "Graph"
    ? GRAPH_DEFAULT_SCOPE
    : combine(resourceOrigin(command.resource), ".default");

const hasExplicitScopes = (command: TokenCommand): boolean =>
  Boolean(command.scopes && command.scopes.length > 0);

export const resolveRequestScopes = (command: TokenCommand): string[] =>
  hasExplicitScopes(command)
    ? (command.scopes ?? []).map((s) =>
        combine(resourceOrigin(command.resource), s)
      )
    : [getDefaultScopeFor(command)];

/**
 * Silent-only variant of {@link getToken}: returns the cached/refreshed token
 * or `null`, never opening a popup. Use it to probe whether a scope is already
 * consented (e.g. from a query function that runs without a user gesture).
 */
export const tryGetTokenSilently = async (
  command: TokenCommand
): Promise<string | null> => {
  await ensureInitialized();

  const account = getAccountForRequest();
  if (!account) return null;

  const authority =
    command.type !== "Graph"
      ? getTenantAuthorityFor(account)
      : msalParams.auth.authority;

  try {
    const result = await app.acquireTokenSilent({
      scopes: resolveRequestScopes(command),
      account,
      authority,
      // A claims challenge makes MSAL bypass its cache, so a picker step-up
      // (CAE) request is not answered with the token it just rejected.
      claims: command.claimsChallenge,
    });
    return result.accessToken || null;
  } catch (error) {
    logger.info("MSAL silent token probe did not yield a token", {
      operation: "tryGetTokenSilently",
      type: command.type,
      resource: command.resource,
      scopes: command.scopes,
      error,
    });
    return null;
  }
};

export const getToken = async (command: TokenCommand): Promise<string> => {
  await ensureInitialized();

  const defaultScope = getDefaultScopeFor(command);
  const requestedScopes = resolveRequestScopes(command);
  const explicitScopes = hasExplicitScopes(command);
  const account = getAccountForRequest();
  // SharePoint tokens (ours and the picker's) come from the account's tenant;
  // Graph uses the multi-tenant `common` authority.
  const authority =
    command.type !== "Graph"
      ? getTenantAuthorityFor(account)
      : msalParams.auth.authority;

  // MSAL bypasses its cache automatically when `claims` is set.
  const authParams: msal.SilentRequest = {
    scopes: requestedScopes,
    account,
    authority,
    forceRefresh: command.type === "SharePoint_SelfIssued",
    claims: command.claimsChallenge,
  };

  let accessToken: string | null = null;
  let lastError: unknown = null;

  try {
    const silentResult = await app.acquireTokenSilent(authParams);
    const grantedScopes = new Set(silentResult.scopes);
    // Only explicit scopes can be "missing"; `.default` expands server-side to
    // whatever is consented, so the server decides what it covers.
    const missingScopes = explicitScopes
      ? requestedScopes.filter((s) => !grantedScopes.has(s))
      : [];

    if (missingScopes.length > 0) {
      logger.warn("MSAL missing scopes — falling back to popup", {
        operation: "getToken",
        type: command.type,
        resource: command.resource,
        missingScopes,
      });
      try {
        accessToken = await acquireTokenViaPopup(requestedScopes, {
          authority,
          claims: command.claimsChallenge,
        });
      } catch (popupError) {
        // Classified (declined vs. failed) once in the terminal block below.
        logger.warn("MSAL consent popup did not complete", {
          operation: "getToken",
          type: command.type,
          resource: command.resource,
          missingScopes,
          error: popupError,
        });
        lastError = popupError;
        accessToken = null;
      }

      // Refresh the `.default` token for this authority after popup consent
      const defaultTokenParams: msal.SilentRequest = {
        scopes: [defaultScope],
        account: app.getActiveAccount() || undefined,
        authority,
        forceRefresh: true,
      };
      try {
        await app.acquireTokenSilent(defaultTokenParams);
      } catch (refreshError) {
        logger.warn("MSAL refreshing .default token failed", {
          operation: "getToken",
          type: command.type,
          resource: command.resource,
          error: refreshError,
        });
      }
    } else {
      accessToken = silentResult.accessToken;
    }
  } catch (e) {
    logger.warn(
      "MSAL silent token acquisition failed — falling back to popup",
      {
        operation: "getToken",
        type: command.type,
        resource: command.resource,
        error: e,
      }
    );
    // popup with SAME authority
    const popupParams: msal.PopupRequest = {
      scopes: requestedScopes,
      authority,
      claims: command.claimsChallenge,
    };
    try {
      const popupResult = await app.acquireTokenPopup(popupParams); // This can show an error in developer console, but its usually chrome being verbose and it still works :)
      app.setActiveAccount(popupResult.account);
      accessToken = popupResult.accessToken || null;

      // refresh the .default token for this authority
      await app
        .acquireTokenSilent({
          scopes: [defaultScope],
          account: app.getActiveAccount() || undefined,
          authority,
          forceRefresh: true,
        })
        .catch(() => {});
    } catch (popupError) {
      // Warn only: if popup fails we still log+throw once from the terminal
      // `if (!accessToken)` block to avoid duplicate exception telemetry.
      logger.warn("MSAL popup token acquisition failed", {
        operation: "getToken",
        type: command.type,
        resource: command.resource,
        scopes: requestedScopes,
        hadExplicitScopes: Boolean(command.scopes?.length),
        error: popupError,
      });
      lastError = popupError;
      accessToken = null;
    }
  }

  if (!accessToken) {
    const errorCode = getMsalErrorCode(lastError);
    const context = {
      operation: "getToken",
      type: command.type,
      resource: command.resource,
      scopes: requestedScopes,
      hadExplicitScopes: explicitScopes,
      errorCode,
    };

    if (isDeclinedMsalError(lastError)) {
      // The user closed the popup, declined consent, or the tenant requires
      // admin approval: a decision, not a defect. Keep it out of the error
      // telemetry that flags real picker problems.
      logger.warn("MSAL token request declined by the user or tenant", context);
      throw new Error(
        `Access token declined (type: ${command.type}, resource: ${command.resource}, code: ${errorCode})`
      );
    }

    const error = new Error(
      `Failed to acquire access token (type: ${command.type}, resource: ${command.resource})`
    );
    logger.error(error.message, { ...context, error });
    throw error;
  }

  return accessToken;
};

export type LinkedMsalAccount = {
  isLinked: boolean;
  name?: string;
  email?: string;
  tenantId?: string;
  homeAccountId?: string;
};

/**
 * Read-only view of the linked Microsoft account. Deliberately does not
 * promote an account to active: this runs inside MSAL event callbacks, and
 * `setActiveAccount` would re-enter them (and silently adopt a second cached
 * account after logout). Promotion happens in `getToken`, where a request needs it.
 */
export const getLinkedAccount = (): LinkedMsalAccount | null => {
  try {
    const account = app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null;
    if (!account) return null;

    const tenantId =
      account.tenantId || account.idTokenClaims?.tid || undefined;

    return {
      isLinked: true,
      name: account.name,
      email: account.username,
      tenantId,
      homeAccountId: account.homeAccountId,
    };
  } catch (err) {
    logger.warn("Reading linked MSAL account failed", {
      operation: "getLinkedAccount",
      error: err,
    });
    return null;
  }
};

export const logoutLinkedAccount = async (): Promise<void> => {
  await ensureInitialized();
  const account =
    app.getActiveAccount() || app.getAllAccounts()[0] || undefined;
  try {
    if (account) {
      await app.logoutPopup({ account }); // This can show an error in developer console, but its usually chrome being verbose and it still works :)
    } else {
      await app.logoutPopup();
    }
  } catch (e) {
    logger.warn("MSAL logoutPopup failed — falling back to logoutRedirect", {
      operation: "logoutLinkedAccount",
      error: e,
    });
    try {
      await app.logoutRedirect({ account });
    } catch (e2) {
      logger.error(
        "MSAL logoutRedirect also failed — user may remain signed in",
        { operation: "logoutLinkedAccount", error: e2 }
      );
    }
  }
};

/** localStorage key holding the Auth0 user id (`sub`) the MSAL cache belongs to. */
export const MSAL_CACHE_OWNER_KEY = "msal.cacheOwner";

const accountChangeListeners = new Set<
  (info: LinkedMsalAccount | null) => void
>();

const notifyAccountChange = () => {
  const linked = getLinkedAccount();
  accountChangeListeners.forEach((listener) => listener(linked));
};

const readCacheOwner = (): string | null => {
  try {
    return localStorage.getItem(MSAL_CACHE_OWNER_KEY);
  } catch {
    return null;
  }
};

/**
 * Ties the MSAL cache to the Auth0 user who is signed in (LAR-401).
 *
 * The cache lives in localStorage so new tabs need no Microsoft popup, but only
 * the manual logout clears it. An expired or SDK-ended Auth0 session leaves the
 * linked Microsoft account behind for whoever signs in next on the same
 * browser. Call this whenever the Auth0 user is known: a cache owned by a
 * different (or unknown) user is dropped, then the current user takes it over.
 * Returns true when the cache was cleared.
 */
export const bindMsalCacheToAuth0User = async (
  auth0UserId: string
): Promise<boolean> => {
  await ensureInitialized();
  const owner = readCacheOwner();
  if (owner === auth0UserId) return false;

  const hadAccounts = app.getAllAccounts().length > 0;
  if (hadAccounts) {
    await app.clearCache();
    logger.info("MSAL cache cleared: linked by another Auth0 user", {
      operation: "bindMsalCacheToAuth0User",
      previousOwnerKnown: owner !== null,
    });
    notifyAccountChange();
  }

  try {
    localStorage.setItem(MSAL_CACHE_OWNER_KEY, auth0UserId);
  } catch (error) {
    logger.warn("Could not record the MSAL cache owner", {
      operation: "bindMsalCacheToAuth0User",
      error,
    });
  }
  return hadAccounts;
};

export const onMsalAccountChange = (
  cb: (info: LinkedMsalAccount | null) => void
): (() => void) => {
  accountChangeListeners.add(cb);
  const handlerId = app.addEventCallback((event) => {
    switch (event.eventType) {
      case msal.EventType.LOGIN_SUCCESS:
      case msal.EventType.ACQUIRE_TOKEN_SUCCESS:
      case msal.EventType.HANDLE_REDIRECT_END:
      case msal.EventType.ACTIVE_ACCOUNT_CHANGED:
      case msal.EventType.LOGOUT_SUCCESS: {
        const linked = getLinkedAccount();
        cb(linked);
        break;
      }
      default:
        break;
    }
  });

  // With a persistent cache, reading accounts before `initialize()` throws;
  // resolve the initial value once MSAL is ready, unless unsubscribed first.
  let disposed = false;
  void ensureInitialized()
    .then(() => {
      if (!disposed) cb(getLinkedAccount());
    })
    .catch(() => {
      if (!disposed) cb(null);
    });

  return () => {
    disposed = true;
    accountChangeListeners.delete(cb);
    if (handlerId) app.removeEventCallback(handlerId);
  };
};
